import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, defaultIfEmpty, map, of, shareReplay, tap } from "rxjs";
import { IUserPermissions } from "../interfaces/access.interface";
import { ConnectionEventsService } from "./connection-events.service";
import { SessionService } from "./session.service";
import { UserService } from "./user.service";

/**
 * Recovers, once per page load, the permissions the user's role currently grants in the
 * organization the session is scoped to, so that those stored in localStorage at login (which may
 * since have changed, e.g. after a role change) are brought up to date when the site is reloaded.
 *
 * Must be instantiated at startup (see initApp), so that it doesn't miss a login happening before
 * it is first used.
 */
@Injectable({
  providedIn: "root",
})
export class PermissionLoaderService {
  /**
   * sessionStorage key marking that the page is being reloaded right after receiving up-to-date
   * permissions. It's kept per tab and survives the reload, then is removed as soon as it is read.
   */
  private readonly upToDateOnReloadIndex = "permissions_up_to_date_on_reload";

  /** Pending or completed load, shared so the permissions are only fetched once per page load. */
  private permissionsLoad$?: Observable<void>;

  constructor(
    private userService: UserService,
    private sessionService: SessionService,
    private connectionEventsService: ConnectionEventsService
  ) {
    // A login (or organization switch) already comes with up-to-date permissions: no need to fetch them
    this.connectionEventsService.login$.subscribe(() => {
      this.permissionsLoad$ = of(undefined);
    });
    this.connectionEventsService.logout$.subscribe(() => {
      this.permissionsLoad$ = undefined;
    });
    // The page was reloaded right after receiving up-to-date permissions (see markUpToDateForReload())
    if (sessionStorage.getItem(this.upToDateOnReloadIndex)) {
      sessionStorage.removeItem(this.upToDateOnReloadIndex);
      this.permissionsLoad$ = of(undefined);
    }
  }

  /**
   * Marks the permissions in the session as up to date for the page reload about to happen (e.g.
   * right after an organization switch), so that they aren't fetched again once reloaded. Only that
   * next reload is affected: any later one fetches them as usual.
   */
  public markUpToDateForReload(): void {
    sessionStorage.setItem(this.upToDateOnReloadIndex, "true");
  }

  /**
   * Fetches the current permissions and stores them in the session, the first time it is called
   * since the page was loaded while logged in. Never fails: if they can't be fetched, the stored ones
   * are kept, except when the user no longer belongs to the current organization, who is logged out.
   * @return Emits once the permissions are up to date, then completes
   */
  public loadPermissions(): Observable<void> {
    // Without a session there's nothing to load, and the request would open the login dialog
    if (!this.sessionService.isLoggedIn) {
      return of(undefined);
    }
    if (!this.permissionsLoad$) {
      const load$: Observable<void> = this.userService.getPermissions().pipe(
        tap((permissions: IUserPermissions) => {
          // A login, organization switch or logout happened while fetching: the response is outdated
          if (this.permissionsLoad$ !== load$) {
            return;
          }
          this.sessionService.currentOrgId = permissions.organizationId;
          this.sessionService.functionalities = permissions.functionalities;
          this.sessionService.routes = permissions.routes;
        }),
        map(() => undefined),
        catchError((err: HttpErrorResponse) => {
          // The user no longer belongs to the organization the session is scoped to
          if (err?.status === 404 && this.permissionsLoad$ === load$) {
            this.connectionEventsService.notifyLogout();
          }
          return of(undefined);
        }),
        // The request completes without emitting when the login dialog it opened is cancelled
        defaultIfEmpty(undefined),
        shareReplay(1)
      );
      this.permissionsLoad$ = load$;
    }
    return this.permissionsLoad$;
  }
}
